import React from 'react'
import bySku from 'utils/productListFetcher/bySku'
import * as gt from 'utils/productShapeTypes'
import { Product } from 'utils/productListFetcher/types'
import useAccount from 'hooks/account/useAccount'
import { isItemChannelRestricted } from 'hooks/account/utils'

export type CustomTailorProduct = {
  objectID: string
  sku: string
  containerID: string
  title: string
  brand?: string
  imageURL: string
  prices: gt.ProductPriceDefinition
}

/**
 * Provides the product data of a custom tailor product form Algolia, part of the product data ("imageURL", "prices"...) get updated if the custom tailor SKU changed but the variant container id hasn't
 *
 * @param variantContainerId container id of the standard variant
 * @param customTailorSku sku of the corresponding custom tailor product of the standard variant
 * @returns a custom tailor product with title, brand, imageUrl...
 */

export default function useCustomTailorProduct(
  variantContainerId: string,
  customTailorSku?: string
) {
  const [product, setProduct] = React.useState<CustomTailorProduct | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [showProduct, setShowProduct] = React.useState<boolean>(true)
  const prevCustomTailorSku = React.useRef(customTailorSku)
  const channel = useAccount().channel

  React.useEffect(() => {
    if (!customTailorSku) {
      setIsLoading(false)
      setShowProduct(false)
      return
    }

    const isEqualCachedSku = prevCustomTailorSku.current == customTailorSku
    const updateCustomTailorData = async () => {
      setIsLoading(isEqualCachedSku)

      const result = await bySku([customTailorSku], 1)(0)
      if (result.data[0] == null) {
        setShowProduct(false)
        setIsLoading(false)
        return
      }
      const productData: Product = result.data[0]

      if (!isEqualCachedSku) {
        prevCustomTailorSku.current = customTailorSku
        setProduct((prev) => ({
          objectID: productData.objectID,
          sku: productData.sku,
          containerID: productData.containerID,
          title: prev?.title || productData.title,
          brand: prev?.brand || productData.brand,
          imageURL: productData.images.imageWeb[0]?.url || '',
          prices: productData.prices
        }))
      } else {
        setProduct(null)
        setProduct({
          objectID: productData.objectID,
          sku: productData.sku,
          containerID: productData.containerID,
          title: productData.title,
          brand: productData?.brand,
          imageURL: productData.images.imageWeb[0]?.url || '',
          prices: productData.prices
        })
      }

      setShowProduct(!isItemChannelRestricted(result.data[0], channel))
      setIsLoading(false)
    }

    updateCustomTailorData()
  }, [customTailorSku, variantContainerId, channel])

  return { product, isLoading, showProduct }
}
